import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, ScrollView } from 'react-native';

import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common } from '@/styles/styles';

import { PlayHistoryRange } from '../hooks/use-play-history';
import { getRangeLabel, selectionToRange } from '../utils/format-range';
import { AccountSummaryRow } from './AccountSummaryRow';
import { ALL_MONTHS, PlayHistoryRangePicker, PlayHistoryRangeSelection } from './PlayHistoryRangePicker';

const CURRENT_YEAR = new Date().getFullYear();

export type AccountHistoryRow = { leftLabel: string; rightLabel: string };

type UseAccountHistoryRows = (range: PlayHistoryRange) => {
    rows: AccountHistoryRow[] | undefined;
    isLoading: boolean;
    isError: boolean;
};

type AccountHistoryContentProps = {
    title: string;
    heading: string;
    useRows: UseAccountHistoryRows;
};

export const AccountHistoryContent = ({ title, heading, useRows }: AccountHistoryContentProps) => {
    const { t } = useTranslation('account_summary');
    const [selection, setSelection] = useState<PlayHistoryRangeSelection>({
        fromYear: CURRENT_YEAR,
        fromMonth: ALL_MONTHS,
        toYear: CURRENT_YEAR,
        toMonth: ALL_MONTHS,
    });

    const { rows, isLoading, isError } = useRows(selectionToRange(selection));
    const rangeLabel = getRangeLabel(selection);

    return (
        <Screen edges={['top', 'bottom']}>
            <ScreenNavBar title={title} />
            <ScrollView style={common.paddingHorizontal}>
                <MaxWidthWrapper paddingHorizontal={'s16'}>
                    <PlayHistoryRangePicker selection={selection} onChange={setSelection} />
                    <SizedBox value={24} />
                    <Text variant="headlineMedium">{heading}</Text>
                    <Text variant="bodyMedium" color={ds.colorToken.text.secondary} textAlign="center" mt="s8" mb="s24">
                        {rangeLabel}
                    </Text>
                    {isLoading ? (
                        <Box alignItems="center" mt="s24">
                            <ActivityIndicator />
                        </Box>
                    ) : isError ? (
                        <Text variant="bodyMedium" color={ds.colorToken.text.secondary}>
                            {t('error')}
                        </Text>
                    ) : rows?.length ? (
                        rows.map(row => (
                            <AccountSummaryRow
                                key={row.leftLabel}
                                leftLabel={row.leftLabel}
                                rightLabel={row.rightLabel}
                            />
                        ))
                    ) : (
                        <Text variant="bodyMedium" color={ds.colorToken.text.secondary}>
                            {t('no_data')}
                        </Text>
                    )}
                </MaxWidthWrapper>
            </ScrollView>
        </Screen>
    );
};
